<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrgStore } from '@/stores/org'
import { useDiscussionStore } from '@/stores/discussion'
import { MessageCircle } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import ChatMessage from '@/pages/Project/Chat/ChatMessage.vue'
import ChatComposer from '@/pages/Project/Chat/ChatComposer.vue'
import { useMentionCandidates } from '@/composables/useMentionCandidates'

const props = defineProps({
  featureCode: { type: String, required: true },
  projectId: { type: [Number, String], required: true },
})

const route = useRoute()
const router = useRouter()
const orgStore = useOrgStore()
const discussionStore = useDiscussionStore()

const projectId = computed(() => props.projectId)
const { mentionCandidates } = useMentionCandidates(projectId)

const thread = computed(() =>
  discussionStore.threadForFeature(props.projectId, props.featureCode)
)

onMounted(() => {
  discussionStore.ensureLoaded(props.projectId)
})

// Keep the thread scrolled to the newest message — on open and as replies land.
const scrollerEl = ref(null)
watch(
  () => [thread.value?.id, thread.value?.replies?.length],
  async () => {
    await nextTick()
    scrollerEl.value?.scrollTo({ top: scrollerEl.value.scrollHeight })
  },
  { immediate: true }
)

// The server replies only when an enabled agent is explicitly @-mentioned.
// Mirror that for the typing indicator.
function noteAgentMention(mentions) {
  const mentioned = (mentions || []).find((m) => m.agent_id)
  if (mentioned?.display_name) {
    discussionStore.notePendingAgent({
      projectId: props.projectId,
      name: mentioned.display_name,
    })
  }
}

async function handleSend({ text, mentions, attachments }) {
  if (thread.value) {
    await discussionStore.sendReply({
      projectId: props.projectId,
      messageId: thread.value.id,
      text,
      mentions,
      attachments,
    })
  } else {
    await discussionStore.sendMessage({
      projectId: props.projectId,
      text,
      mentions,
      attachments,
      featureCode: props.featureCode,
    })
  }
  noteAgentMention(mentions)
}

function jumpToChat() {
  discussionStore.setActiveThread(thread.value.id)
  router.push({
    name: 'ProjectChat',
    params: {
      org_id: route.params.org_id || orgStore.selectedOrg,
      project_id: props.projectId,
    },
  })
}
</script>

<template>
  <div>
    <div class="mb-1 flex items-center justify-between">
      <h3 class="text-base font-bold">Messages</h3>
      <Button
        v-if="thread"
        variant="ghost"
        size="sm"
        class="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
        @click="jumpToChat"
      >
        <MessageCircle class="size-3.5" />
        Jump to chat
      </Button>
    </div>
    <div v-if="thread" ref="scrollerEl" class="max-h-64 overflow-y-auto py-1">
      <ChatMessage :message="thread" variant="popup" />
    </div>
    <div :class="thread ? 'border-t pt-2' : ''">
      <ChatComposer
        :members="mentionCandidates"
        :data-attachments="false"
        :pin-option="false"
        :placeholder="thread ? 'Reply' : undefined"
        @send="handleSend"
      />
    </div>
  </div>
</template>
